/* =========================================================================
 * THE HUNT REQUEST — one implementation, every surface.
 *
 * Brian: "My team shouldn't have to type the championship into that search, we
 * should be able to pushbutton it and pull and record."
 * Brian: "the hand hunt on the subscription is the route every one of these
 * tasks must take."
 *
 * ⚠️ THIS MODULE NO LONGER REACHES A METERED SEARCH, AND THAT IS THE POINT.
 *
 * It used to own start / poll / accept against POST /api/champion-hunt, which
 * bills per record through Claude or OpenAI. Two surfaces called it: the row
 * button and the selection bar — so a drag-select of fifty rows was fifty paid
 * searches behind one click, with no estimate and no ceiling in front of it.
 * When it refused, the backend's own message told the operator to set
 * ANTHROPIC_API_KEY, i.e. the app was instructing its users to open the hole.
 *
 * Brian's rule is absolute: "Nothing should be routed through paid api with
 * Claude or OpenAI." So the metered path is not merely switched off behind a
 * flag here — the client-side machinery for calling it has been REMOVED, and
 * the `championHunt*` helpers were removed from lib/api.js with it. A flag can
 * be flipped by anybody in a hurry; a call that does not exist cannot be.
 *
 * What remains is the free route. `requestHunt` marks the record for the
 * hand-hunt sweep, which runs on a subscription SR already pays for and works
 * requested records FIRST. Same one press on the row; different place the work
 * happens, and no per-record cost.
 *
 * ONE IMPLEMENTATION, because two implementations of one job is how the weaker
 * one quietly wins — the same failure this codebase has already had with two
 * folder normalizers and two champion classifiers.
 * ========================================================================= */

import { api } from '../../../lib/api.js'

/**
 * REQUEST a hunt — the free route, and the only one any button uses.
 *
 * Marks the record for the hand-hunt sweep. Already-queued comes back as a
 * SUCCESS with `alreadyQueued`, because two reps pressing the same button
 * should not produce a failure for the second one. A record that already names
 * a champion is REFUSED with that champion named — putting a finished record
 * back in the queue is how a good crown gets overwritten.
 */
export async function requestHunt(id, note) {
  const key = String(id || '').trim()
  if (!key) throw new Error('No record to queue.')
  return api.championRequest(key, note)
}

export function hostOf(u) {
  try { return new URL(u).hostname.replace(/^www\./i, '') } catch { return String(u || '').slice(0, 32) }
}

/**
 * Can this row be hunted at all?
 *
 * Only a championship can be crowned. A pursuit-adapted row (RFP, corporate,
 * police, cheer) carries `sourceType` and has no championship to look up, so
 * offering the button there is a control that can only waste somebody's time.
 * An id is required because the request is keyed on the record, not on a name —
 * which is the whole reason the rep no longer types anything.
 */
export function canHunt(record) {
  const c = record || {}
  /* ⚠️ AND A RETIRED PHANTOM HAS NO CHAMPION TO FIND. Readiness already reports
   * these as CANNOT BE CROWNED; queueing one hands the hunter a record whose
   * only possible outcome is "searched, nothing found" — which is exactly the
   * bucket already running at a 9.4% hit rate. Same reasoning as `sourceType`:
   * a control that can only waste somebody's time. */
  return Boolean(c.id) && !c.sourceType && !c.retired
}
